import { memo, useCallback } from 'react';

import { type AirActionTrackingLocation } from '@air/analytics';
import { type ShortUrlResponse } from '@air/api/types';
import { Modal, ModalDescription } from '@air/primitive-modal';

import { RegenerateShareLinkButtons } from '~/components/SharePrivateContentModal/RegenerateLink/RegenerateShareLinkButtons';
import { ShareModalHeader } from '~/components/SharePrivateContentModal/ShareModalHeader';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useRegenerateShortIdForCollection } from '~/swr-hooks/shortUrl/useRegenerateShortIdForCollection';

export type SharePrivateCollectionRegenerateProps = {
  collectionId: string;
  trackLocation: AirActionTrackingLocation;
  shortIdInfo: ShortUrlResponse;
  onRegenerated: () => void;
  onCancel: () => void;
};

export const SharePrivateCollectionRegenerate = memo(
  ({
    collectionId,
    trackLocation,
    shortIdInfo,
    onClose,
    onCancel,
    onRegenerated,
  }: AirModalProps<SharePrivateCollectionRegenerateProps>) => {
    const { currentWorkspace } = useCurrentWorkspace();
    const { regenerateShortIdForCollection } = useRegenerateShortIdForCollection();

    const handleDismiss = useCallback(() => {
      onClose();
      onCancel();
    }, [onClose, onCancel]);

    const onRegenerate = useCallback(() => {
      const workspaceId = currentWorkspace?.id;
      if (!workspaceId) {
        throw new Error('No workspace id');
      }
      regenerateShortIdForCollection.mutate(
        {
          workspaceId,
          collectionId,
          shortId: shortIdInfo.id,
          permissions: shortIdInfo.permissions,
          trackLocation,
        },
        {
          onSuccess: () => {
            onRegenerated();
          },
        },
      );
    }, [
      currentWorkspace?.id,
      collectionId,
      onRegenerated,
      regenerateShortIdForCollection,
      shortIdInfo.id,
      shortIdInfo.permissions,
      trackLocation,
    ]);

    return (
      <Modal isOpen onDismiss={handleDismiss}>
        <ShareModalHeader onClose={handleDismiss} title="Generate new link?" />
        <ModalDescription>
          This will break the current URL and people with the old link will no longer have access to the collection. Are
          you sure you want to generate a new URL?
        </ModalDescription>
        <RegenerateShareLinkButtons
          isLoading={regenerateShortIdForCollection.isPending}
          onRegenerate={onRegenerate}
          onClose={handleDismiss}
        />
      </Modal>
    );
  },
);

SharePrivateCollectionRegenerate.displayName = 'SharePrivateCollectionRegenerate';
